/**
 * Import d'un SVG de créature retouché (Inkscape, Figma…) → spec `cards.creature`
 * (PUR hormis `DOMParser`, testé).
 *
 * Le fichier n'est JAMAIS injecté tel quel : on n'en lit que la GÉOMÉTRIE
 * (`<polygon>` et `<path>` à segments droits) et la couleur de remplissage,
 * puis la spec est revalidée par `cardCreatureSchema`. Scripts, images, textes,
 * styles et liens sont ignorés.
 *
 * - Calques : `<g id="evolved-back|base|evolved-front">` (ceux de l'export) ;
 *   un pli hors de ces groupes va dans la base.
 * - Transformations (translate / scale / rotate / matrix) des éléments et de
 *   leurs groupes appliquées ; viewBox ramenée à 0..100 (échelle uniforme).
 * - Couleur : la nuance d'origine (`data-shade`) est gardée si la couleur n'a
 *   pas été modifiée ; sinon on retrouve une nuance de la teinte si la couleur
 *   en est une (la créature continue de suivre le niveau), sinon couleur fixe.
 */

import {
  CREATURE_LIMITS,
  CREATURE_NAMED_COLORS,
  cardCreatureSchema,
  shadeHex,
  type CardCreature,
  type CreatureFill,
  type CreatureLayer,
  type CreatureNamedColor,
  type CreaturePoly,
} from '@jeraisonne/db/cards';

export const CREATURE_SVG_MAX_BYTES = 200_000;

export type CreatureImportResult =
  | { ok: true; creature: CardCreature }
  | { ok: false; error: string };

type Matrix = [number, number, number, number, number, number];
const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

function multiply(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

const nums = (s: string) => (s.match(/-?(?:\d+\.?\d*|\.\d+)(?:e-?\d+)?/gi) ?? []).map(Number);

/** `transform="translate(…) scale(…) rotate(…) matrix(…)"` → matrice. */
export function parseTransform(value: string | null): Matrix {
  if (!value) return IDENTITY;
  let m = IDENTITY;
  for (const [, fn, args] of value.matchAll(/(\w+)\s*\(([^)]*)\)/g)) {
    const a = nums(args ?? '');
    let t: Matrix = IDENTITY;
    switch (fn) {
      case 'translate':
        t = [1, 0, 0, 1, a[0] ?? 0, a[1] ?? 0];
        break;
      case 'scale':
        t = [a[0] ?? 1, 0, 0, a[1] ?? a[0] ?? 1, 0, 0];
        break;
      case 'rotate': {
        const r = ((a[0] ?? 0) * Math.PI) / 180;
        const [cx, cy] = [a[1] ?? 0, a[2] ?? 0];
        const rot: Matrix = [Math.cos(r), Math.sin(r), -Math.sin(r), Math.cos(r), 0, 0];
        t = multiply(multiply([1, 0, 0, 1, cx, cy], rot), [1, 0, 0, 1, -cx, -cy]);
        break;
      }
      case 'matrix':
        if (a.length === 6) t = a as Matrix;
        break;
      default:
        break;
    }
    m = multiply(m, t);
  }
  return m;
}

type Pt = [number, number];

/** Chemin à segments droits (M/L/H/V/Z, absolus et relatifs) → sous-chemins. */
export function pathToPolygons(d: string): Pt[][] | { error: string } {
  const tokens = d.match(/[a-zA-Z]|-?(?:\d+\.?\d*|\.\d+)(?:e-?\d+)?/g) ?? [];
  const polys: Pt[][] = [];
  let cur: Pt[] = [];
  let x = 0;
  let y = 0;
  let cmd = '';
  let i = 0;
  const next = () => Number(tokens[i++]);
  while (i < tokens.length) {
    const tok = tokens[i]!;
    if (/[a-zA-Z]/.test(tok)) {
      cmd = tok;
      i++;
      if (/[CcSsQqTtAa]/.test(cmd)) {
        return { error: 'Le SVG contient des courbes : convertis-les en segments droits.' };
      }
      if (cmd === 'Z' || cmd === 'z') {
        if (cur.length) polys.push(cur);
        cur = [];
        continue;
      }
      if (cmd !== 'M' && cmd !== 'm' && cmd !== 'L' && cmd !== 'l' && !/[HhVv]/.test(cmd)) {
        return { error: `Commande de chemin non prise en charge : « ${cmd} ».` };
      }
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    switch (cmd.toUpperCase()) {
      case 'M':
        if (cur.length) polys.push(cur);
        x = rel ? x + next() : next();
        y = rel ? y + next() : next();
        cur = [[x, y]];
        // Paires suivantes d'un M = lignes.
        cmd = rel ? 'l' : 'L';
        break;
      case 'L':
        x = rel ? x + next() : next();
        y = rel ? y + next() : next();
        cur.push([x, y]);
        break;
      case 'H':
        x = rel ? x + next() : next();
        cur.push([x, y]);
        break;
      case 'V':
        y = rel ? y + next() : next();
        cur.push([x, y]);
        break;
      default:
        i++;
    }
  }
  if (cur.length) polys.push(cur);
  return polys;
}

/** Retire les sommets doublons et alignés (Inkscape en ajoute en retouchant). */
export function simplifyPolygon(pts: Pt[]): Pt[] {
  let out = pts.filter(
    (p, i) => i === 0 || Math.hypot(p[0] - pts[i - 1]![0], p[1] - pts[i - 1]![1]) > 0.05,
  );
  if (
    out.length > 1 &&
    Math.hypot(out[0]![0] - out.at(-1)![0], out[0]![1] - out.at(-1)![1]) <= 0.05
  ) {
    out = out.slice(0, -1);
  }
  let changed = true;
  while (changed && out.length > 3) {
    changed = false;
    for (let i = 0; i < out.length; i++) {
      const a = out[(i - 1 + out.length) % out.length]!;
      const b = out[i]!;
      const c = out[(i + 1) % out.length]!;
      const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      if (Math.abs(cross) < 0.02) {
        out.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  return out;
}

function normalizeHex(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${[...v.slice(1)].map((c) => c + c).join('')}`;
  const m = v.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (m) {
    return `#${m
      .slice(1, 4)
      .map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0'))
      .join('')}`;
  }
  if (v === 'black') return '#000000';
  if (v === 'white') return '#ffffff';
  return null;
}

function hexDistance(a: string, b: string): number {
  let d = 0;
  for (const i of [1, 3, 5])
    d += Math.abs(parseInt(a.slice(i, i + 2), 16) - parseInt(b.slice(i, i + 2), 16));
  return d;
}

/** Couleur d'un pli importé → nuance / couleur nommée / hex fixe. */
export function fillFromSvg(
  hex: string,
  accent: string,
  hints: { shade?: number; named?: string },
): CreatureFill {
  if (hints.shade !== undefined && hexDistance(hex, shadeHex(accent, hints.shade)) <= 6) {
    return Math.round(hints.shade * 100) / 100;
  }
  if (hints.named && hints.named in CREATURE_NAMED_COLORS) {
    const named = hints.named as CreatureNamedColor;
    if (hexDistance(hex, CREATURE_NAMED_COLORS[named].toLowerCase()) <= 6) return named;
  }
  for (const [name, value] of Object.entries(CREATURE_NAMED_COLORS)) {
    if (hexDistance(hex, value.toLowerCase()) <= 6) return name as CreatureNamedColor;
  }
  let best = { s: 0, d: Infinity };
  for (let k = -60; k <= 85; k++) {
    const s = k / 100;
    const d = hexDistance(hex, shadeHex(accent, s));
    if (d < best.d) best = { s, d };
  }
  return best.d <= 9 ? best.s : hex;
}

const LAYERS: CreatureLayer[] = ['evolved-back', 'base', 'evolved-front'];
const SKIP_ANCESTORS = new Set(['defs', 'clippath', 'mask', 'pattern', 'symbol', 'marker']);

function styleFill(el: Element): string | null {
  const style = el.getAttribute('style') ?? '';
  const m = style.match(/(?:^|;)\s*fill\s*:\s*([^;]+)/i);
  return m ? m[1]!.trim() : el.getAttribute('fill');
}

/** Lit un SVG de créature. `accent` = teinte courante de la carte (pour retrouver les nuances). */
export function svgToCreature(svgText: string, accent: string): CreatureImportResult {
  if (svgText.length > CREATURE_SVG_MAX_BYTES) {
    return { ok: false, error: 'Fichier trop lourd (200 ko maximum).' };
  }
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const root = doc.documentElement;
  if (
    !root ||
    root.nodeName.toLowerCase() !== 'svg' ||
    doc.getElementsByTagName('parsererror').length
  ) {
    return { ok: false, error: 'Ce fichier n’est pas un SVG valide.' };
  }

  const vb = nums(root.getAttribute('viewBox') ?? '');
  const [vx, vy, vw, vh] =
    vb.length === 4
      ? vb
      : [
          0,
          0,
          Number(root.getAttribute('width')) || 100,
          Number(root.getAttribute('height')) || 100,
        ];
  const scale = 100 / Math.max(vw!, vh!);
  const offX = (100 - vw! * scale) / 2;
  const offY = (100 - vh! * scale) / 2;

  const layers: Record<CreatureLayer, CreaturePoly[]> = {
    'evolved-back': [],
    base: [],
    'evolved-front': [],
  };
  let tooManyVertices = 0;

  for (const el of Array.from(root.querySelectorAll('polygon, path'))) {
    let matrix = parseTransform(el.getAttribute('transform'));
    let layer: CreatureLayer = 'base';
    let layerFound = false;
    let skip = false;
    for (let p = el.parentElement; p && p !== root; p = p.parentElement) {
      const name = p.nodeName.toLowerCase();
      if (SKIP_ANCESTORS.has(name)) skip = true;
      matrix = multiply(parseTransform(p.getAttribute('transform')), matrix);
      const id = (p.getAttribute('id') ?? p.getAttribute('inkscape:label') ?? '').toLowerCase();
      if (!layerFound && (LAYERS as string[]).includes(id)) {
        layer = id as CreatureLayer;
        layerFound = true;
      }
    }
    if (skip) continue;

    const rawFill = styleFill(el);
    if (rawFill && rawFill.trim().toLowerCase() === 'none') continue;
    const hex = normalizeHex(rawFill ?? 'black');
    if (!hex) continue;

    let shapes: Pt[][];
    if (el.nodeName.toLowerCase() === 'polygon') {
      const n = nums(el.getAttribute('points') ?? '');
      const pts: Pt[] = [];
      for (let k = 0; k + 1 < n.length; k += 2) pts.push([n[k]!, n[k + 1]!]);
      shapes = [pts];
    } else {
      const parsed = pathToPolygons(el.getAttribute('d') ?? '');
      if ('error' in parsed) return { ok: false, error: parsed.error };
      shapes = parsed;
    }

    const shadeAttr = el.getAttribute('data-shade');
    const fill = fillFromSvg(hex, accent, {
      shade:
        shadeAttr !== null && Number.isFinite(Number(shadeAttr)) ? Number(shadeAttr) : undefined,
      named: el.getAttribute('data-color') ?? undefined,
    });

    for (const shape of shapes) {
      const mapped = simplifyPolygon(
        shape.map(([x, y]) => {
          const tx = matrix[0] * x + matrix[2] * y + matrix[4];
          const ty = matrix[1] * x + matrix[3] * y + matrix[5];
          return [(tx - vx!) * scale + offX, (ty - vy!) * scale + offY] as Pt;
        }),
      );
      if (mapped.length < CREATURE_LIMITS.minVertices) continue;
      if (mapped.length > CREATURE_LIMITS.maxVertices) {
        tooManyVertices++;
        continue;
      }
      const fmt = (n: number) => String(Math.round(n * 100) / 100);
      layers[layer].push({ points: mapped.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(' '), fill });
    }
  }

  if (tooManyVertices > 0) {
    return {
      ok: false,
      error: `${tooManyVertices} forme(s) ont plus de ${CREATURE_LIMITS.maxVertices} sommets : découpe-les en plis plus simples.`,
    };
  }

  const title = root.querySelector('title')?.textContent?.trim();
  const result = cardCreatureSchema.safeParse({
    species: (title || 'créature').slice(0, 40),
    base: layers.base,
    evolvedBack: layers['evolved-back'],
    evolvedFront: layers['evolved-front'],
  });
  if (!result.success) {
    const count = layers.base.length;
    if (count < CREATURE_LIMITS.minBase) {
      return { ok: false, error: `Il faut au moins ${CREATURE_LIMITS.minBase} plis dans la base.` };
    }
    if (count > CREATURE_LIMITS.maxBase) {
      return {
        ok: false,
        error: `Trop de plis dans la base (${CREATURE_LIMITS.maxBase} maximum).`,
      };
    }
    if (
      layers['evolved-back'].length > CREATURE_LIMITS.maxEvolved ||
      layers['evolved-front'].length > CREATURE_LIMITS.maxEvolved
    ) {
      return {
        ok: false,
        error: `Trop de plis d’évolution (${CREATURE_LIMITS.maxEvolved} maximum par calque).`,
      };
    }
    return { ok: false, error: 'Certaines formes sortent du cadre ou sont mal formées.' };
  }
  return { ok: true, creature: result.data };
}
